import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ArgonButton from "components/ArgonButton";
import edushalaLogo from "assets/images/edushala.png";
import axios from "utils/api";
import { toast } from "react-toastify";
import "./styles.css";

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    height: 54, borderRadius: "12px", backgroundColor: "#f8fafc", color: "#14213d",
    transition: "all 180ms ease", "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused": { backgroundColor: "#fff", boxShadow: "0 0 0 4px rgba(37, 99, 235, .09)" },
    "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: "1px" },
  },
  "& input": {
    width: 0,
    minWidth: 0,
    flex: "1 1 auto",
    fontSize: ".94rem",
    padding: "15px 14px",
  },
};

function Illustration() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = (event) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    axios.post("/user/login", { email: email.trim(), password })
      .then((res) => {
        if (!res.data.success) {
          toast.error(res.data.message || "Login failed!");
          return;
        }
        const { token, refreshToken, user } = res.data.data;
        localStorage.setItem("token", token);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
        toast.success(res.data.message);
        if (user.role === "SUPER_ADMIN") {
          localStorage.setItem("allowedPermissions", JSON.stringify(["ALL"]));
          navigate("/dashboard");
        } else if (user.role === "ADMIN") {
          localStorage.setItem("allowedPermissions", JSON.stringify(user.permissions));
          navigate("/");
        } else {
          toast.error("You do not have access to the admin portal.");
        }
      })
      .catch((err) => toast.error(err.response?.data?.error || "Unable to sign in. Please try again."))
      .finally(() => setLoading(false));
  };

  return (
    <main className="edu-login-page">
      <div className="edu-login-shell">
        <section className="edu-login-form-panel">
          <div className="edu-brand">
            <img src={edushalaLogo} alt="EduShala Academy" className="edu-brand-logo" />
          </div>
          <div className="edu-login-heading">
            <h1>Welcome back!</h1>
            <p>Sign in to manage your EduShala learning community.</p>
          </div>
          <form onSubmit={handleLogin} noValidate>
            <label htmlFor="email">Email address</label>
            <TextField id="email" type="email" placeholder="Enter your email"
              value={email} onChange={(event) => setEmail(event.target.value)} fullWidth
              autoComplete="email" autoFocus sx={fieldStyles}
              InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineRoundedIcon className="edu-field-icon" /></InputAdornment> }}
            />
            <label htmlFor="password" className="edu-password-label">Password</label>
            <TextField id="password" type={showPassword ? "text" : "password"}
              placeholder="Enter your password" value={password}
              onChange={(event) => setPassword(event.target.value)} fullWidth
              autoComplete="current-password" sx={fieldStyles}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockRoundedIcon className="edu-field-icon" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((visible) => !visible)} edge="end" size="small">
                      {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className="edu-remember">
              <Checkbox checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)}
                size="small" sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#2563eb" } }} />
              <span>Remember me</span>
            </div>
            <ArgonButton type="submit" disabled={loading} fullWidth className="edu-login-button">
              {loading ? <CircularProgress size={23} color="inherit" /> : "Sign in to dashboard"}
            </ArgonButton>
          </form>
          <div className="edu-secure-note"><LockRoundedIcon /> Secure access for authorized staff only</div>
        </section>

        <aside className="edu-login-visual">
          <div className="edu-grid-pattern" /><div className="edu-visual-ring" />
          <header>
            <span>LEARN • GROW • SUCCEED</span>
            <h2>Shaping brighter futures, one lesson at a time.</h2>
            <p>Everything your team needs to connect students with the right educators and learning opportunities.</p>
          </header>
          <div className="edu-learning-art">
            <div className="edu-art-orbit" />
            <div className="edu-art-center"><SchoolRoundedIcon /></div>
            <div className="edu-float-card edu-students-card">
              <i><GroupsRoundedIcon /></i>
              <span><strong>Students</strong><small>Learn with confidence</small></span>
            </div>
            <div className="edu-float-card edu-educators-card">
              <i><AutoStoriesRoundedIcon /></i>
              <span><strong>Educators</strong><small>Inspire every day</small></span>
            </div>
          </div>
          <footer><span />Empowering the next generation through education</footer>
        </aside>
      </div>
    </main>
  );
}

export default Illustration;
